import { Link } from "react-router-dom";
import { ROUTES } from "../../../lib/constants";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-dark-primary text-text-primary font-inter">
      {/* Nav */}
      <nav className="border-b border-dark-border/30 bg-dark-primary/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={ROUTES.LANDING} className="text-xl font-black font-outfit tracking-tighter text-golden-accent">
            SuBs
          </Link>
          <Link to={ROUTES.LANDING} className="text-sm text-text-secondary hover:text-golden-accent transition-colors">
            ← Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-golden-accent mb-3">Legal</p>
          <h1 className="text-4xl md:text-5xl font-black font-outfit tracking-tighter text-text-primary mb-4">
            Privacy Policy
          </h1>
          <p className="text-text-secondary">Last updated: March 2026</p>
        </div>

        <div className="space-y-10 text-text-secondary leading-relaxed">

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">1. Information We Collect</h2>
            <p className="mb-3">We collect the following types of information:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong className="text-text-primary">Account data:</strong> Name, email address, username, and password (hashed)</li>
              <li><strong className="text-text-primary">Transaction data:</strong> Payment records, subscription history, and amounts</li>
              <li><strong className="text-text-primary">Platform identifiers:</strong> Telegram user ID, Discord ID, Slack ID (where applicable)</li>
              <li><strong className="text-text-primary">KYC data:</strong> If KYC is enabled by your admin, identity verification data submitted through the subscription flow</li>
              <li><strong className="text-text-primary">Usage data:</strong> Log data, IP addresses, and access timestamps</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">2. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>To authenticate and manage your account</li>
              <li>To process payments and manage subscriptions</li>
              <li>To grant and revoke access to connected communities</li>
              <li>To send transactional emails (receipts, expiry notices)</li>
              <li>To improve Platform reliability and performance</li>
              <li>To comply with legal and regulatory obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">3. Data Sharing</h2>
            <p className="mb-3">We do not sell your personal data. We share data only with:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong className="text-text-primary">Razorpay:</strong> Payment processing. Governed by Razorpay's privacy policy.</li>
              <li><strong className="text-text-primary">Verification provider:</strong> identity verification support, only when KYC is enabled by the admin.</li>
              <li><strong className="text-text-primary">Admins:</strong> Your subscription status and platform IDs are visible to the admin of the community you subscribe to.</li>
              <li><strong className="text-text-primary">Legal authorities:</strong> If required by law or valid legal process.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">4. Data Retention</h2>
            <p>
              We retain account data for as long as your account is active. Transaction records are retained for a minimum of 7 years for compliance purposes. You may request deletion of non-transactional personal data by contacting support.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">5. Security</h2>
            <p>
              We use industry-standard encryption for data in transit (TLS) and for sensitive credentials at rest. Passwords are hashed using bcrypt. API secrets are encrypted before storage. No system is completely secure; please use strong, unique passwords.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">6. Cookies</h2>
            <p>
              We use only essential cookies and session tokens required for authentication. We do not use tracking cookies or third-party advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">7. Your Rights</h2>
            <p className="mb-3">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your account and non-transactional data</li>
              <li>Object to or restrict processing in certain circumstances</li>
            </ul>
            <p className="mt-3">To exercise these rights, contact us via the support page.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">8. Third-Party Links</h2>
            <p>
              The Platform may contain links to third-party services (Telegram, Discord, Slack). We are not responsible for the privacy practices of these services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this policy periodically. We will notify you of significant changes via email or an in-app notice. Continued use of the Platform constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-text-primary mb-3">10. Contact</h2>
            <p>
              For privacy concerns or data requests, visit our{" "}
              <Link to={ROUTES.SUPPORT} className="text-golden-accent hover:underline">
                support page
              </Link>
              .
            </p>
          </section>

        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-dark-border/30 py-8 mt-16">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-text-secondary">
          <p>© {new Date().getFullYear()} SuBs. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to={ROUTES.TERMS} className="hover:text-golden-accent transition-colors">Terms</Link>
            <Link to={ROUTES.PRIVACY} className="hover:text-golden-accent transition-colors">Privacy</Link>
            <Link to={ROUTES.SUPPORT} className="hover:text-golden-accent transition-colors">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
